'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { ChevronDown } from 'lucide-react';
import type { ContactMessengerType } from '@/lib/profileRegistrationForm';
import { AppButton, AppInput } from '@/components/design/forms';
import { cn } from '@/components/ui/utils';

const MESSENGER_HOME_URL: Record<'telegram' | 'whatsapp' | 'instagram', string> = {
  telegram: 'https://t.me/',
  whatsapp: 'https://www.whatsapp.com/',
  instagram: 'https://www.instagram.com/',
};

interface ContactInputProps {
  type: ContactMessengerType;
  value: string;
  onTypeChange: (type: ContactMessengerType) => void;
  onValueChange: (value: string) => void;
  error?: string;
  className?: string;
  required?: boolean;
  /** Показать «Выберите» и сброс типа (значение очищается снаружи при сбросе). */
  allowEmpty?: boolean;
  /** Крупные поля как на странице профиля (border-2, py-3, rounded-xl). */
  large?: boolean;
  /**
   * Узкий триггер и отступы — в общей сетке с email/phone/city, чтобы блок помещался в одну строку.
   */
  compact?: boolean;
}

export function ContactInput({
  type,
  value,
  onTypeChange,
  onValueChange,
  error,
  className = '',
  required,
  allowEmpty = false,
  large = false,
  compact = false,
}: ContactInputProps) {
  const t = useTranslations('strings.contactInput');
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [menuBox, setMenuBox] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    setMounted(true);
  }, []);

  const syncMenuPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuBox({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    syncMenuPosition();
    const ro = new ResizeObserver(syncMenuPosition);
    if (triggerRef.current) ro.observe(triggerRef.current);
    window.addEventListener('scroll', syncMenuPosition, true);
    window.addEventListener('resize', syncMenuPosition);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', syncMenuPosition, true);
      window.removeEventListener('resize', syncMenuPosition);
    };
  }, [isOpen, syncMenuPosition]);

  const contactTypes = [
    { value: 'telegram' as const, label: 'Telegram', short: 'TG' },
    { value: 'whatsapp' as const, label: 'WhatsApp', short: 'WA' },
    { value: 'instagram' as const, label: 'Instagram', short: 'IG' },
  ];

  const selectedType = contactTypes.find((ct) => ct.value === type);
  const buttonLabel = allowEmpty && !type ? t('selectMessenger') : (selectedType?.label ?? 'Telegram');
  const buttonLabelShort =
    allowEmpty && !type ? t('selectMessenger') : (selectedType?.short ?? 'TG');

  const getPlaceholder = () => {
    switch (type || 'telegram') {
      case 'telegram':
        return '@username';
      case 'whatsapp':
        return '+994 50 123 45 67';
      case 'instagram':
        return '@username';
      default:
        return '@username';
    }
  };

  const controlText = large ? 'text-base' : 'text-sm';
  const controlPadY = compact ? (large ? 'py-2.5' : 'py-1.5') : large ? 'py-3' : 'py-1.5';
  const controlPadInput = compact
    ? large
      ? 'px-3 py-2.5'
      : 'px-2 py-1.5'
    : large
      ? 'px-4 py-3'
      : 'px-2.5 py-1.5';
  const controlRadius = large ? 'rounded-xl' : 'rounded-lg';
  const borderOuter = large ? 'border-2' : 'border';
  const triggerMinW = compact
    ? large
      ? 'min-w-0 w-[min(100%,7.25rem)] max-w-[8.25rem] sm:w-auto sm:min-w-[7.5rem] sm:max-w-[9rem]'
      : 'min-w-0 w-[min(100%,6.5rem)] max-w-[7.5rem] sm:min-w-[7rem]'
    : large
      ? 'min-w-[11rem]'
      : 'min-w-[9.25rem]';
  const chevronClass = large ? 'h-4 w-4' : 'h-3.5 w-3.5';
  const minHControl = compact ? 'min-h-[2.375rem]' : 'min-h-[2.5rem]';

  const inputDisabled = allowEmpty && !type;

  const triggerShell = compact
    ? 'border-r border-fest-border-warm/90 bg-gradient-to-b from-fest-canvas to-fest-border-warm/15'
    : 'border-r border-fest-border-warm bg-fest-canvas';

  const outerRing = error
    ? 'border-fest-red'
    : 'border-fest-border-strong focus-within:border-fest-gold';

  return (
    <div className={cn('min-w-0 w-full max-w-full', className)}>
      <div
        className={cn(
          'flex w-full min-w-0 overflow-hidden bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] transition-colors',
          controlRadius,
          borderOuter,
          outerRing,
          inputDisabled && 'opacity-70',
        )}
      >
        <div
          ref={triggerRef}
          className={cn('relative flex min-w-0 shrink-0', triggerShell, triggerMinW)}
        >
          <AppButton
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              'box-border flex h-full min-h-0 w-full min-w-0 flex-1 items-center justify-between gap-1.5 font-medium text-fest-heading transition-colors',
              compact ? 'hover:bg-fest-border-warm/50' : 'bg-fest-canvas hover:bg-fest-border-warm/80',
              controlText,
              compact ? (large ? 'px-2.5' : 'px-2') : large ? 'px-4' : 'px-2.5',
              controlPadY,
              minHControl,
              'rounded-none border-0',
            )}
          >
            <span className="min-w-0 flex-1 truncate text-left">
              <span className="sm:hidden">{compact ? buttonLabelShort : buttonLabel}</span>
              <span className="hidden sm:inline">{buttonLabel}</span>
            </span>
            <ChevronDown className={cn('shrink-0 text-fest-subtle-text', chevronClass)} aria-hidden />
          </AppButton>
        </div>

        <AppInput
          type="text"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          required={required}
          disabled={inputDisabled}
          className={cn(
            'box-border min-w-0 flex-1 rounded-none border-0 bg-transparent text-fest-heading placeholder-fest-subtle-text outline-none ring-0 transition-colors focus:border-0 focus:ring-0 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-fest-subtle-text',
            minHControl,
            controlText,
            controlPadInput,
          )}
          placeholder={inputDisabled ? t('placeholderDisabled') : getPlaceholder()}
        />
      </div>

      {mounted &&
        isOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[10040]"
              onClick={() => setIsOpen(false)}
              aria-hidden
            />
            <div
              className={cn(
                'fixed z-[10050] min-w-0 overflow-hidden border border-fest-border-strong bg-white shadow-lg',
                large ? 'rounded-xl' : 'rounded-lg',
              )}
              style={{
                top: menuBox.top,
                left: menuBox.left,
                minWidth: menuBox.width,
              }}
              role="listbox"
              aria-label={t('selectMessenger')}
            >
              {allowEmpty && (
                <AppButton
                  type="button"
                  onClick={() => {
                    onTypeChange('');
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-2.5 text-left text-sm transition-colors ${
                    !type ? 'bg-fest-gold text-white' : 'text-fest-heading hover:bg-fest-canvas'
                  }`}
                >
                  {t('notSpecified')}
                </AppButton>
              )}
              {contactTypes.map((ct) => (
                <a
                  key={ct.value}
                  href={MESSENGER_HOME_URL[ct.value]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'block w-full cursor-pointer px-3 py-2.5 text-left text-sm no-underline transition-colors',
                    type === ct.value
                      ? 'bg-fest-gold text-white'
                      : 'text-fest-heading hover:bg-fest-canvas',
                  )}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
                      return;
                    }
                    e.preventDefault();
                    onTypeChange(ct.value);
                    setIsOpen(false);
                  }}
                >
                  {ct.label}
                </a>
              ))}
            </div>
          </>,
          document.body,
        )}

      {error ? <p className="mt-1 text-xs text-fest-red">{error}</p> : null}
    </div>
  );
}
